import React from 'react';
import {
  Box, LinearProgress, Paper, Skeleton, Typography,
} from '@mui/material';
import { PETCADEMY_SHADOW } from '../../utilities';
import { percentOf } from './engagementMath';

interface ChannelBreakdownCardProps {
  loading: boolean;
  engaged: number;
  engagedLessons: number;
  engagedMessages: number;
  engagedSurveys: number;
}

// Share of engaged pet parents per channel. The denominator is engaged pet parents, not
// supported ones, and a parent can count in several bars, so they need not sum to 100%.
export default function ChannelBreakdownCard({
  loading, engaged, engagedLessons, engagedMessages, engagedSurveys,
}: ChannelBreakdownCardProps) {
  const bars = [
    { label: 'Reviewed a lesson', value: percentOf(engagedLessons, engaged) },
    { label: 'Messaged a trainer', value: percentOf(engagedMessages, engaged) },
    { label: 'Submitted a survey', value: percentOf(engagedSurveys, engaged) },
  ];

  return (
    <Paper sx={{ p: 2 }} elevation={PETCADEMY_SHADOW}>
      <Typography variant="subtitle2" component="p" sx={{ mb: 2 }}>
        {loading ? <Skeleton width="40%" /> : `Share of the ${engaged.toLocaleString()} engaged pet parents who:`}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {bars.map((bar) => (
          <Box key={bar.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" id={`channel-${bar.label}`}>{bar.label}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {loading ? <Skeleton width={32} /> : `${bar.value}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={loading ? 0 : bar.value}
              aria-labelledby={`channel-${bar.label}`}
              // Neutral track, as the design system's HorizontalBarChart draws it.
              sx={{ height: 8, borderRadius: '999px', bgcolor: 'rgba(0, 0, 0, 0.08)' }}
            />
          </Box>
        ))}
      </Box>
      <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 2 }}>
        Pet parents can appear in more than one channel.
      </Typography>
    </Paper>
  );
}
